import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Goal, Target, Rocket, CalendarClock, Flag, TrendingUp, TrendingDown, Minus, Gauge, Layers,
} from 'lucide-react';
import { useTenant } from '@/context/TenantContext';
import api from '@/lib/api';
import { usePost } from '@/lib/useData';
import { PageHeader } from '@/components/common/PageHeader';
import { SectionCard } from '@/components/common/SectionCard';
import { ScoreGauge } from '@/components/common/ScoreGauge';
import { StatusPill } from '@/components/common/StatusPill';
import { EmptyState } from '@/components/common/EmptyState';
import { CardSkeleton, GridSkeleton } from '@/components/common/Loading';
import { TrendAreaChart } from '@/components/charts/TrendAreaChart';
import { ComparisonBars } from '@/components/charts/ComparisonBars';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { fmtInt, fmtPct, fmtNum } from '@/lib/format';

const ACCENT = 'var(--tenant-accent-500)';
const NEUTRAL = '#C7CDD6';

const WEEK_STATUS = {
  atteint: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  attention: 'bg-amber-50 text-amber-800 ring-amber-200',
  retard: 'bg-rose-50 text-rose-700 ring-rose-200',
  en_cours: 'accent-bg-soft accent-text',
  a_venir: 'bg-muted text-muted-foreground ring-transparent',
};
const WEEK_STATUS_LABEL = {
  atteint: 'Atteint', attention: 'Attention', retard: 'Retard', en_cours: 'En cours', a_venir: 'À venir',
};

function Kpi({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="rounded-2xl border bg-card p-4" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <Icon className="h-4 w-4" /><span className="text-xs">{label}</span>
      </div>
      <p className="mt-1 font-display text-2xl font-semibold tabular text-foreground">{value}</p>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

function TrendIcon({ trend }) {
  if (trend === 'positive') return <TrendingUp className="h-4 w-4 text-emerald-600" />;
  if (trend === 'negative') return <TrendingDown className="h-4 w-4 text-rose-600" />;
  return <Minus className="h-4 w-4 text-muted-foreground" />;
}

function AdminOverview() {
  const { data, isLoading } = useQuery({
    queryKey: ['/objectives/overview'],
    queryFn: async () => (await api.post('/objectives/overview')).data,
  });
  if (isLoading) return <GridSkeleton count={5} />;
  const items = data?.objectives || [];
  if (!items.length) return null;
  return (
    <SectionCard title="Toutes les associations" subtitle="Avancement annuel consolidé" testid="objectives-overview">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
        {items.map((o) => {
          const pct = Math.round((o.pct || 0) * 100);
          const inactive = o.active === false;
          return (
            <div key={o.association_code} className={`rounded-2xl border bg-card p-4 ${inactive ? 'opacity-60' : ''}`} data-testid="objective-overview-card">
              <div className="flex items-center justify-between">
                <span className="break-words pr-2 text-sm font-medium" title={o.association_name}>{o.association_name}</span>
                {!inactive && <StatusPill status={o.status} compact />}
              </div>
              {inactive ? (
                <>
                  <p className="mt-2 text-sm font-medium text-muted-foreground">Non actif cette année</p>
                  <p className="text-xs text-muted-foreground">Aucune activité — hors objectif global</p>
                  <div className="mt-2 h-1.5 w-full rounded-full bg-muted" />
                </>
              ) : (
                <>
                  <p className="mt-2 font-display text-2xl font-semibold tabular accent-text">{pct}%</p>
                  <p className="text-xs text-muted-foreground">{fmtInt(o.achieved)} / {fmtInt(o.target)} BS validés</p>
                  <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                    <div className="h-full rounded-full" style={{ width: `${Math.min(pct, 100)}%`, background: ACCENT }} />
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}

export default function Objectifs() {
  const { association, isAll, isCampaignManager } = useTenant();
  const code = association?.code;
  const { data, isLoading } = usePost('/objectives/weekly', code ? { association: code } : null);
  const [showFuture, setShowFuture] = useState(false);

  const tracking = data?.tracking;
  const annual = data?.annual;
  const objective = data?.objective;

  const trajectory = useMemo(() => (tracking?.weeks || []).map((w) => ({
    wk: w.period.includes('-S') ? `S${w.period.split('-S')[1]}` : w.label,
    realise: w.cumul_realized,
    objectif: w.cumul_target,
  })), [tracking]);

  const weeklyBars = useMemo(() => (tracking?.weeks || [])
    .filter((w) => !w.is_future)
    .map((w) => ({ wk: `S${w.period.split('-S')[1]}`, realise: w.realized, objectif: Math.round(w.objective) })),
  [tracking]);

  const tableWeeks = useMemo(() => (tracking?.weeks || [])
    .filter((w) => (showFuture ? true : !w.is_future)), [tracking, showFuture]);

  return (
    <div className="space-y-6" data-testid="objectifs-page">
      <PageHeader
        title="Objectifs"
        subtitle={isAll
          ? 'Avancement annuel consolidé — toutes les associations'
          : isCampaignManager
            ? (objective?.label || 'Votre objectif personnel')
            : (association ? `Suivi de l'objectif — ${association.short_name || association.name}` : 'Suivi des objectifs')}
        icon={Goal}
      />

      {isAll ? (
        <AdminOverview />
      ) : isLoading ? (
        <><GridSkeleton count={4} /><CardSkeleton /></>
      ) : !data?.has_objective ? (
        <EmptyState
          title="Aucun objectif défini"
          body={isCampaignManager
            ? "Aucun objectif personnel ne vous a encore été assigné. Votre administrateur SHARITI peut le paramétrer dans Administration → Objectifs (portée « Chargé de campagne »)."
            : "Définissez un objectif pour cette association depuis Administration → Objectifs."}
        />
      ) : (
        <>
          {/* Hero */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <SectionCard title="Avancement" subtitle={objective?.label} className="lg:col-span-1" testid="objective-hero">
              <div className="flex flex-col items-center">
                <ScoreGauge score={Math.round((annual.pct || 0) * 100)} rating="" size={190} />
                <div className="mt-3">
                  <StatusPill status={annual.status} />
                </div>
              </div>
            </SectionCard>

            <div className="grid grid-cols-2 gap-4 lg:col-span-2">
              <Kpi icon={Target} label="Objectif" value={fmtInt(annual.target)} sub="BS validés" testid="kpi-target" />
              <Kpi icon={Flag} label="Réalisé" value={fmtInt(annual.achieved)} sub={`${fmtPct(annual.pct)} de l'objectif`} testid="kpi-achieved" />
              <Kpi icon={Layers} label="Reste à faire" value={fmtInt(annual.remaining)} sub={`${annual.days_remaining} jours restants`} testid="kpi-remaining" />
              <Kpi icon={Rocket} label="Projection fin de période" value={fmtInt(annual.projection)}
                sub={annual.projection >= annual.target ? 'Objectif dépassé estimé' : 'En-deçà de l’objectif estimé'} testid="kpi-projection" />
              <div className="col-span-2 grid grid-cols-2 gap-4">
                <Kpi icon={Gauge} label="Rythme actuel" value={`${fmtNum(annual.current_pace, 1)} /j`}
                  sub={`Rythme requis : ${fmtNum(annual.required_pace, 1)} /j`} testid="kpi-pace" />
                <Kpi icon={CalendarClock} label="À faire / semaine" value={fmtNum(tracking.summary.next_week_needed, 0)}
                  sub={`sur ${tracking.summary.remaining_weeks} semaine(s)`} testid="kpi-nextweek" />
              </div>
            </div>
          </div>

          {/* Pace & trend chips */}
          <div className="flex flex-wrap items-center gap-3 rounded-2xl accent-bg-soft px-4 py-3" data-testid="objective-pace">
            <span className="inline-flex items-center gap-1.5 text-sm font-medium">
              <Gauge className="h-4 w-4 accent-text" /> {tracking.summary.pace_label}
            </span>
            <span className="text-muted-foreground">·</span>
            <span className="inline-flex items-center gap-1.5 text-sm font-medium">
              <TrendIcon trend={tracking.summary.trend} /> {tracking.summary.trend_label}
            </span>
            <span className="text-muted-foreground">·</span>
            <span className="text-sm text-muted-foreground">
              Objectif hebdomadaire : <span className="font-medium text-foreground tabular">{fmtInt(tracking.weekly_target)} BS/sem.</span>
            </span>
          </div>

          {/* Trajectory */}
          <SectionCard title="Trajectoire cumulée" subtitle="Réalisé cumulé vs objectif cumulé, semaine par semaine" testid="objective-trajectory">
            <TrendAreaChart
              data={trajectory}
              xKey="wk"
              height={280}
              series={[
                { key: 'realise', name: 'Réalisé cumulé', color: ACCENT },
                { key: 'objectif', name: 'Objectif cumulé', color: NEUTRAL },
              ]}
              formatTip={(v) => `${fmtInt(v)} BS`}
            />
          </SectionCard>

          {/* Weekly bars */}
          <SectionCard title="Réalisé vs objectif hebdomadaire" subtitle="Semaines écoulées" testid="objective-weekly-bars">
            {weeklyBars.length ? (
              <ComparisonBars
                data={weeklyBars}
                xKey="wk"
                height={260}
                series={[
                  { key: 'realise', name: 'Réalisé', color: ACCENT },
                  { key: 'objectif', name: 'Objectif', color: NEUTRAL },
                ]}
                formatTip={(v) => `${fmtInt(v)} BS`}
              />
            ) : <EmptyState body="Aucune semaine écoulée sur la période." />}
          </SectionCard>

          {/* Weekly table */}
          <SectionCard
            title="Détail hebdomadaire"
            subtitle="Objectif, réalisé, taux d'atteinte, écart et cumul par semaine"
            action={
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <Switch checked={showFuture} onCheckedChange={setShowFuture} data-testid="obj-show-future" />
                Semaines à venir
              </label>
            }
            bodyClassName="pt-0"
            testid="objective-weekly-table-card">
            <ScrollArea className="h-[360px] rounded-xl border">
              <Table data-testid="objective-weekly-table">
                <TableHeader className="sticky top-0 z-10 bg-card">
                  <TableRow>
                    <TableHead>Semaine</TableHead>
                    <TableHead className="text-right">Objectif</TableHead>
                    <TableHead className="text-right">Réalisé</TableHead>
                    <TableHead className="text-right">Taux</TableHead>
                    <TableHead className="text-right">Écart</TableHead>
                    <TableHead className="text-right">Cumul</TableHead>
                    <TableHead>Statut</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tableWeeks.map((w) => (
                    <TableRow key={w.period} className={w.is_current ? 'accent-bg-soft' : ''} data-testid="objective-week-row">
                      <TableCell className="font-medium">{w.label}</TableCell>
                      <TableCell className="text-right tabular text-muted-foreground">{fmtInt(w.objective)}</TableCell>
                      <TableCell className="text-right tabular font-medium">{w.is_future ? '—' : fmtInt(w.realized)}</TableCell>
                      <TableCell className="text-right tabular">{w.is_future ? '—' : fmtPct(w.taux)}</TableCell>
                      <TableCell className={`text-right tabular ${!w.is_future && w.ecart < 0 ? 'text-rose-600' : (!w.is_future && w.ecart > 0 ? 'text-emerald-600' : 'text-muted-foreground')}`}>
                        {w.is_future ? '—' : `${w.ecart > 0 ? '+' : ''}${fmtInt(w.ecart)}`}
                      </TableCell>
                      <TableCell className="text-right tabular text-muted-foreground">{fmtInt(w.cumul_realized)}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${WEEK_STATUS[w.status]}`}>
                          {WEEK_STATUS_LABEL[w.status]}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          </SectionCard>
        </>
      )}
    </div>
  );
}
